import React, { useMemo } from 'react';
import { getLocalStorage } from '@shared/core';
import { CurrencySelectorV2 } from '@shared/components/CurrencySelectorV2';
import { useGeoBasedLocale, CURRENCY_PARAMS, isValidCurrencyCode, updateLocaleCurrency, updateLocaleSearchParams } from '@shared/hooks/useGeoLocale';
import { isTravelCurrencyCode } from '@shared/utils/currency';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useCurrencyCodeFromBrowserLocale } from '@shared/utils/currency/useCurrencyCodeFromBrowseLocale';
import { TRAVEL_DEFAULT_CURRENCY_CODES } from './travelCurrencies';
import { useTravelCurrencyAllowlist } from '@shared/hooks/useTravelCurrencyAllowlist';
import type { TravelCurrencySelectorProps } from './TravelCurrencySelector.types';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { useTravelCurrencyPickHandlerFromContext } from './TravelCurrencyPickHandlerContext';

// Dedicated storage key for travel currency — separate from joyLocale so
// the locale sync pipeline (useI18nLocaleSyncer) doesn't overwrite non-legacy currencies.
export const TRAVEL_CURRENCY_STORAGE_KEY = 'joyTravelCurrency';

export { parseCurrencyPayload } from './parseCurrencyPayload';

const useTravelCurrency = (allowedSet: ReadonlySet<string>): CurrencyCodeType => {
  const { currency: localeCurrency } = useGeoBasedLocale();
  const { getValueString } = useQueryParamHelper();

  // URL param takes highest priority — allows shared links to override stored preference.
  // Must also exist in the flag-controlled allowlist to prevent unsupported currencies.
  const urlCurrency = getValueString(CURRENCY_PARAMS);
  if (urlCurrency && isTravelCurrencyCode(urlCurrency) && allowedSet.has(urlCurrency)) {
    return urlCurrency as CurrencyCodeType;
  }

  const stored = getLocalStorage().getItem(TRAVEL_CURRENCY_STORAGE_KEY);
  if (stored && isTravelCurrencyCode(stored) && allowedSet.has(stored)) {
    return stored as CurrencyCodeType;
  }

  // localeCurrency may itself originate from the URL param (via useGeoBasedLocale),
  // so it must also be validated against the allowlist to prevent bypass.
  if (allowedSet.has(localeCurrency)) {
    return localeCurrency;
  }

  return 'USD' as CurrencyCodeType;
};

/**
 * Applies a travel currency the same way the picker does: store the preference, update the
 * locale params, reload so every price re-quotes. Shared so other surfaces (the cash-fund
 * currency prompt on the hotel PDP) switch identically instead of re-implementing it.
 */
/**
 * The currency the travel pages are pricing in right now: URL param, then the stored pick, then the
 * geo locale, each checked against the allowlist. For surfaces that price without rendering the
 * selector (the trip board).
 */
export const useActiveTravelCurrency = (): CurrencyCodeType => {
  const { allowedSet } = useTravelCurrencyAllowlist();
  return useTravelCurrency(allowedSet);
};

export const useSetTravelCurrency = (): ((currencyCode: CurrencyCodeType) => void) =>
  useEventCallback((currencyCode: CurrencyCodeType) => {
    getLocalStorage().setItem(TRAVEL_CURRENCY_STORAGE_KEY, currencyCode);

    if (isValidCurrencyCode(currencyCode)) {
      updateLocaleCurrency(currencyCode);
    } else {
      updateLocaleSearchParams({ currency: currencyCode }, { addParam: 'currency' });
    }

    withWindow(global => global.location.reload());
  });

export const TravelCurrencySelector: React.FC<TravelCurrencySelectorProps> = ({
  isEnabled,
  mode = 'button',
  buttonAppearance,
  value: controlledValue,
  onChange: controlledOnChange,
  disabled,
  placeholder,
  restrictToCurrencyCodes,
  hideFlag = true,
}) => {
  const browserCurrency = useCurrencyCodeFromBrowserLocale();
  const { allCurrencyCodes, popularCurrencyCodes: payloadPopularCodes, allowedSet, loading: isFFLoading } = useTravelCurrencyAllowlist();

  const restrictionSet = useMemo(() => (restrictToCurrencyCodes ? new Set<string>(restrictToCurrencyCodes) : undefined), [restrictToCurrencyCodes]);
  const restrictedAllCurrencyCodes = useMemo(
    () => (restrictionSet ? allCurrencyCodes.filter(code => restrictionSet.has(code)) : allCurrencyCodes),
    [allCurrencyCodes, restrictionSet],
  );
  const restrictedAllowedSet = useMemo(() => (restrictionSet ? new Set([...allowedSet].filter(code => restrictionSet.has(code))) : allowedSet), [allowedSet, restrictionSet]);

  const internalCurrency = useTravelCurrency(restrictedAllowedSet);

  // Build popular codes: browser-locale currency first, then the payload/default popular list, deduplicated
  const popularCurrencyCodes = useMemo((): ReadonlyArray<CurrencyCodeType> => {
    const allCodesSet = new Set<CurrencyCodeType>(restrictedAllCurrencyCodes);
    const basePopularCodes = payloadPopularCodes ?? TRAVEL_DEFAULT_CURRENCY_CODES;
    const codes: CurrencyCodeType[] = [];
    const seen = new Set<CurrencyCodeType>();

    if (browserCurrency && allCodesSet.has(browserCurrency as CurrencyCodeType)) {
      codes.push(browserCurrency as CurrencyCodeType);
      seen.add(browserCurrency as CurrencyCodeType);
    }

    for (const code of basePopularCodes) {
      if ((!restrictionSet || restrictionSet.has(code)) && !seen.has(code)) {
        codes.push(code);
        seen.add(code);
      }
    }

    return codes;
  }, [browserCurrency, payloadPopularCodes, restrictedAllCurrencyCodes, restrictionSet]);

  const currencies = useMemo(
    () => ({
      popularCurrencyCodes,
      allCurrencyCodes: restrictedAllCurrencyCodes,
    }),
    [popularCurrencyCodes, restrictedAllCurrencyCodes],
  );

  const handleInternalChange = useSetTravelCurrency();
  const contextOnChange = useTravelCurrencyPickHandlerFromContext();

  if (!isEnabled || isFFLoading) return null;

  return (
    <CurrencySelectorV2<CurrencyCodeType>
      value={controlledValue ?? internalCurrency}
      onChange={controlledOnChange ?? contextOnChange ?? handleInternalChange}
      mode={mode}
      buttonAppearance={buttonAppearance}
      hideFlag={hideFlag}
      disabled={disabled}
      placeholder={placeholder}
      currencies={currencies}
    />
  );
};

TravelCurrencySelector.displayName = 'TravelCurrencySelector';
